import { Component, Input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { TimeModel } from '../../models/time.model';
import moment from 'moment-timezone';
import * as _ from 'underscore';
import { timeFormatsEnum } from '../../../../Core/Users/enums/time-formats.enum';
import { TwelveHourModel } from '../../models/twelve-hour.model';
import { TwentyFourHourModel } from '../../models/twenty-four-hour.model';
import { TimeHelper } from '../../helpers/time.helper';
import { MinuteModel } from '../../models/minute.model';
import { MessageModel } from '../../../Misc/models/message.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { DeliveryRecurrencePatternModel } from '../../models/delivery-recurrence-pattern.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { DeliveryRecurrenceFrequencyEnum } from '../../enums/delivery-recurrence-frequency.enum';
import { Subscription, filter } from 'rxjs';
import { DeliverySchedule, ScheduleType } from './models/delivery-schedule';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { trigger, transition, style, animate } from '@angular/animations';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

export const deliverySchedulerComponentText = {
    selectADate: 'Select a date'
};

@Component({
    selector: 'agpr-delivery-schedule-input',
    templateUrl: './delivery-schedule-input.component.html',
    styleUrls: ['./delivery-schedule-input.component.scss'],
    providers: [getProvider(DeliveryScheduleInputComponent)],
    animations: [
        trigger('slideInOut', [
            transition(':enter', [
                style({ opacity: 0, height: 0, overflow: 'hidden' }),
                animate('0.3s ease-in', style({ opacity: 1, height: '*' }))
            ]),
            transition(':leave', [
                style({ opacity: 1, height: '*', overflow: 'hidden' }),
                animate('0.3s ease-in', style({ opacity: 0, height: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class DeliveryScheduleInputComponent extends ControlValueAccessorModel<DeliverySchedule> implements OnDestroy {
    public readonly messageSeverityEnum = messageSeverityEnum;

    @Input() id: string;
    @Input() localization = deliverySchedulerComponentText;
    // Required for accessibility unless the element is placed in a panel with a heading that describes the input
    // I.e. If in a panel labelled Scheduling the header is not required
    @Input() header: string = null;
    @Input() minDate: Date = new Date();
    @Input() disabled: boolean = false;
    @Input() immediateOptionLabel: string = null;
    @Input() futureOptionLabel: string = null;
    @Input() minDateValidationMessage: string = null;
    @Input() infoIconStyleClass: string = null;
    @Input() infoTooltipStyleClass: string = null;
    @Input() showRecurring: boolean = false;
    @Input() recurringOptionLabel: string = null;
    @Input() messages: MessageModel[] = null;
    @Input() disclaimer: TemplateRef<any>;

    recurringModelValue: DeliveryRecurrencePatternModel;
    @Input()
    get recurringModel(): DeliveryRecurrencePatternModel {
        return this.recurringModelValue;
    }
    set recurringModel(inputModel: DeliveryRecurrencePatternModel) {
        if (inputModel.times.length) {
            if (this.areMultipleTimeValuesInUse(inputModel.frequencyType)) {
                this.recurringMultiTimes = inputModel.times;
            } else {
                this.recurringTime = inputModel.times[0];
            }
        }

        this.recurringModelValue = inputModel;
    }

    calendarDate: Date = null;
    debouncedUpdateDeliveryDate = _.debounce(() => this.scheduleModified(), 150);

    recurringMultiTimes: TimeModel[] = [
        new TimeModel()
    ];
    recurringTime: TimeModel = new TimeModel();
    daysOfTheWeek = DeliveryRecurrencePatternModel.DAYS_OF_THE_WEEK_OPTIONS;

    public readonly daysOfTheMonthOptions = DeliveryRecurrencePatternModel.DAYS_OF_THE_MONTH_OPTIONS;
    public deliveryRecurrenceFrequencyEnum = DeliveryRecurrenceFrequencyEnum;
    private subscriptions: Subscription[] = [];

    @ViewChild('calendar') calendar: any;

    public readonly scheduleTypes = ScheduleType;
    time: TimeModel = null;
    userTimeZonePreference: string = null;
    timeFormat: timeFormatsEnum;

    errors: MessageModel[];

    constructor(
        private timeHelper: TimeHelper,
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService
    ) {
        super();

        this.subscriptions.push(
            this.localizationService.getLocalizationMapChange().subscribe(
                () => {
                    this.daysOfTheWeek.forEach(
                        day => {
                            day.label = this.localizationService.transform(day.localizationKey);
                        }
                    );
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    this.userTimeZonePreference = preferences.timeZone;
                    this.timeFormat = preferences.timeFormat;
                }
            )
        );
    }

    writeValue(value: DeliverySchedule): void {
        if (value) {
            this.value = value;

            if (this.value.scheduleType === this.scheduleTypes.scheduledDate && this.value.scheduledDateUTC) {
                this.time = this.timeHelper.getTimeFromDate(this.value.scheduledDateUTC, this.value.timeZone, this.timeFormat);
            } else {
                // If immediate mode, or no specified schedule date yet
                this.time = this.timeHelper.getTimeFromDate(new Date(), this.userTimeZonePreference, this.timeFormat);
            }
            this.updateCalendarFromDeliveryDate();
            this.errors = this.getErrorMessages();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    scheduleTypeChanged(type: ScheduleType) {
        this.value.scheduleType = type;
        this.onModelChange(this.value);
        this.errors = this.getErrorMessages();
    }

    scheduleModified(): void {
        if (this.value.scheduleType === ScheduleType.scheduledDate) {
            // Moment only accepts 24 hour formatted hours, so the format is converted
            const deliveryDate: Date = this.value.scheduledDateUTC || this.calendarDate;
            const twentyFourHourTime: TimeModel = this.timeHelper.changeFormat(this.time, timeFormatsEnum.twentyFourHour);
            const month: number = moment(this.calendarDate).month();
            const day: number = moment(this.calendarDate).date();
            const year: number = moment(this.calendarDate).year();
            const hour: TwelveHourModel | TwentyFourHourModel = twentyFourHourTime.hour;
            const minute: MinuteModel = this.time.minute;
            let newDeliveryDate: moment.Moment;

            if (deliveryDate && this.calendarDate instanceof Date) {
                newDeliveryDate = moment(deliveryDate);

                newDeliveryDate.tz(this.time.timeZone);
                newDeliveryDate.year(year);
                newDeliveryDate.month(month);
                newDeliveryDate.date(day);
                newDeliveryDate.hour(hour);
                newDeliveryDate.minute(minute);

                this.value.scheduledDateUTC = newDeliveryDate.toDate();
                this.value.timeZone = this.time.timeZone;
            }
        } else {
            this.value.timeZone = this.userTimeZonePreference;
        }
        this.onModelChange(this.value);
        this.errors = this.getErrorMessages();
    }

    updateRecurringPattern(): void {
        if (this.areMultipleTimeValuesInUse()) {
            this.recurringModel.times = this.recurringMultiTimes.slice();
            this.recurringModel.timeZone = this.recurringMultiTimes[0].timeZone;
        } else {
            this.recurringModel.times = [this.recurringTime];
            this.recurringModel.timeZone = this.recurringTime.timeZone;
        }
        this.errors = this.getErrorMessages();
        // this.recurrencePatternChanged.emit(this.recurringModel);
    }

    /**
    * Called when the recurring type changes, emits event for parent to react to
    */
    updateRecurringType(type: string): void {
        if (type === this.recurringModel.frequencyType) {
            return;
        }
        this.recurringModel.frequencyType = type;

        if (this.areMultipleTimeValuesInUse()) {
            this.recurringModel.times = [
                this.timeHelper.changeFormat(
                    this.recurringMultiTimes[0],
                    timeFormatsEnum.twentyFourHour
                )
            ];
        } else {
            this.recurringModel.times = [
                this.timeHelper.changeFormat(
                    this.recurringTime,
                    timeFormatsEnum.twentyFourHour
                )
            ];
        }

        if (this.recurringModel.frequencyType === DeliveryRecurrenceFrequencyEnum.daily) {
            // For daily, choose all the days of the week
            this.recurringModel.frequencyValues = [0, 1, 2, 3, 4, 5, 6];
        } else {
            this.recurringModel.frequencyValues = [];
        }

        // this.recurrencePatternChanged.emit(this.recurringModel);
    }

    getErrorMessages(): MessageModel[] {
        let messages: MessageModel[] = [];
        let tooEarlyMessage: MessageModel = null;

        if (this.messages) {
            messages = this.messages.filter(message => message.severity === messageSeverityEnum.error);
        }

        if (this.value.scheduleType === ScheduleType.scheduledDate && this.value.scheduledDateUTC < this.minDate && this.minDateValidationMessage) {
            tooEarlyMessage = {
                severity: messageSeverityEnum.error,
                summary: this.minDateValidationMessage
            };

            messages.push(tooEarlyMessage);
        }

        return messages;
    }

    openCalendar(event: Event) {
        this.calendar.showOverlay();
        event.stopPropagation();
    }

    /**
    * Creates a date object from the delivery date, but where
    * the month and day will reflect the time in the selected timezone
    * instead of the time zone of the user's computer
    */
    private updateCalendarFromDeliveryDate(): void {

        const deliveryDateMoment: moment.Moment = moment(this.value.scheduledDateUTC);
        const calendarDateMoment: moment.Moment = moment(new Date());

        deliveryDateMoment.tz(this.time.timeZone);

        calendarDateMoment.month(deliveryDateMoment.month());
        calendarDateMoment.date(deliveryDateMoment.date());
        calendarDateMoment.year(deliveryDateMoment.year());

        this.calendarDate = calendarDateMoment.toDate();

    }

    /**
     * Determine if the internal structure to store time values are an array or not, based upon the recurring frequency
     *
     * Currently only Daily recurring types use an array for times - update this logic if other frequencies allow
     * for multiple time values.
     *
     * @param frequencyType
     */
    private areMultipleTimeValuesInUse(frequencyType: string = null): boolean {
        if (frequencyType === null) {
            return this.recurringModel.frequencyType === DeliveryRecurrenceFrequencyEnum.daily;
        } else {
            return frequencyType === DeliveryRecurrenceFrequencyEnum.daily;
        }
    }
}
